import { Reducer } from 'react';

import { KindOf, TypeForKind } from '@/utilities/HasKind';

import { Actions } from '../actions/Actions';
import { AddTrackingEvent } from '../actions/AddTrackingEvent';
import { ActivateComplete } from '../actions/completions/ActivateComplete';
import { InitializeComplete } from '../actions/completions/InitializeComplete';
import { UpgradeComplete } from '../actions/completions/UpgradeComplete';
import { FeatureFlagsChanged } from '../actions/FeatureFlagsChanged';
import { Destroy, kind as destroy } from '../actions/goals/Destroy';
import { Disable, kind as disable } from '../actions/goals/Disable';
import { createGoal } from '../actions/goals/Goal';
import { kind as logOut, LogOut } from '../actions/goals/LogOut';
import { kind as rebuild, Rebuild } from '../actions/goals/Rebuild';
import { kind as restart, Restart } from '../actions/goals/Restart';
import { kind as start, Start } from '../actions/goals/Start';
import { kind as stop, Stop } from '../actions/goals/Stop';
import { NetworkChanged } from '../actions/NetworkChanged';
import { SelectBorer } from '../actions/SelectBorer';
import { addGoal, reject, rejectAll, resolve } from '../GoalResolvers';
import { State } from '../State';
import { idle } from './lifecycle/idle';
import { kind as activate } from './lifecycle/start';
import { kind as suspend } from './lifecycle/suspend';

export type ActionReducers = {
  [K in KindOf<Actions>]: Reducer<State, TypeForKind<Actions, K>>;
};

// Most actions have properties that will be applied directly to the State,
// including resolving goals after completing a task.
// These then impact the life cycle.
// Compute the resulting state before applying life cycle changes.

export function addTrackingEvent(state: State, action: AddTrackingEvent): State {
  return {
    ...state,
    trackingEvents: [...state.trackingEvents, action.trackingEvent],
  };
}

export function afterActivate(state: State, action: ActivateComplete): State {
  let goalResolvers = state.goalResolvers;
  goalResolvers = resolve(goalResolvers, start);
  return {
    ...state,
    currentTask: undefined,
    goalResolvers,
    replication: action.replication,
    nextTask: undefined,
  };
}

export function afterClean(state: State): State {
  let goalResolvers = state.goalResolvers;
  // Cleaning effectively destroys the database,
  // so we'll resolve those goals now.
  goalResolvers = resolve(goalResolvers, destroy);
  goalResolvers = resolve(goalResolvers, stop);

  let disabled = state.disabled;
  if (state.goalResolvers.Rebuild.length) {
    // A rebuild is the only thing that overrides a disable.
    disabled = false;
  }
  return {
    ...state,
    currentTask: undefined,
    cleaned: true,
    disabled,
    goalResolvers,
    nextTask: undefined,
  };
}

export function afterDestroy(state: State): State {
  let goalResolvers = state.goalResolvers;
  goalResolvers = resolve(goalResolvers, destroy);
  goalResolvers = resolve(goalResolvers, stop);
  return {
    ...state,
    currentTask: undefined,
    database: undefined,
    goalResolvers,
    nextTask: undefined,
  };
}

export function afterInitialize(state: State, action: InitializeComplete): State {
  let goalResolvers = state.goalResolvers;
  goalResolvers = resolve(goalResolvers, start);
  goalResolvers = resolve(goalResolvers, rebuild);
  goalResolvers = resolve(goalResolvers, restart);
  goalResolvers = resolve(goalResolvers, stop);
  return {
    ...state,
    cleaned: false,
    currentTask: undefined,
    database: action.database,
    goalResolvers,
    nextTask: undefined,
  };
}

export function afterSuspend(state: State): State {
  let goalResolvers = state.goalResolvers;
  goalResolvers = resolve(goalResolvers, stop);
  goalResolvers = resolve(goalResolvers, restart);

  return {
    ...state,
    currentTask: undefined,
    goalResolvers,
    nextTask: undefined,
    replication: undefined,
  };
}

export function afterUpgrade(state: State, action: UpgradeComplete): State {
  let goalResolvers = state.goalResolvers;
  goalResolvers = resolve(goalResolvers, stop);
  if (action.database) {
    goalResolvers = resolve(goalResolvers, start);
    goalResolvers = resolve(goalResolvers, restart);
  }
  return {
    ...state,
    cleaned: false,
    currentTask: undefined,
    database: action.database,
    disabled: action.disabled,
    goalResolvers,
    legacy: false,
    nextTask: undefined,
  };
}

export function afterWipe(state: State): State {
  let goalResolvers = state.goalResolvers;
  goalResolvers = resolve(goalResolvers, logOut);
  goalResolvers = resolve(goalResolvers, destroy);
  goalResolvers = rejectAll(goalResolvers, 'Wiped');
  return idle({
    ...state,
    cleaned: true,
    currentTask: undefined,
    goalResolvers,
    nextTask: undefined,
  });
}

export function clearTrackingEvents(state: State): State {
  return {
    ...state,
    trackingEvents: [],
  };
}

export function onDestroy(state: State, action: Destroy): State {
  let goalResolvers = state.goalResolvers;
  goalResolvers = addGoal(goalResolvers, action);
  const reason = 'Destroying';
  goalResolvers = reject(goalResolvers, start, reason);
  goalResolvers = reject(goalResolvers, restart, reason);
  return {
    ...state,
    goalResolvers,
  };
}

export function onDisable(state: State, action: Disable): State {
  let goalResolvers = state.goalResolvers;
  goalResolvers = addGoal(goalResolvers, action);
  const reason = 'Disabling';
  goalResolvers = reject(goalResolvers, rebuild, reason);
  goalResolvers = reject(goalResolvers, start, reason);
  goalResolvers = reject(goalResolvers, restart, reason);
  return {
    ...state,
    goalResolvers,
  };
}

export function onLogOut(state: State, action: LogOut): State {
  let goalResolvers = state.goalResolvers;
  goalResolvers = addGoal(goalResolvers, action);
  return {
    ...state,
    goalResolvers,
  };
}

export function onRebuild(state: State, action: Rebuild): State {
  let goalResolvers = state.goalResolvers;
  goalResolvers = addGoal(goalResolvers, action);
  const reason = 'Rebuilding';
  goalResolvers = reject(goalResolvers, start, reason);
  goalResolvers = reject(goalResolvers, restart, reason);
  goalResolvers = reject(goalResolvers, disable, reason);
  return {
    ...state,
    goalResolvers,
  };
}

export function onRestart(state: State, action: Restart): State {
  let goalResolvers = state.goalResolvers;
  goalResolvers = addGoal(goalResolvers, action);
  const reason = 'Restarting';
  goalResolvers = reject(goalResolvers, start, reason);
  goalResolvers = reject(goalResolvers, stop, reason);
  return {
    ...state,
    goalResolvers,
  };
}

export function onStart(state: State, action: Start): State {
  let goalResolvers = state.goalResolvers;
  goalResolvers = addGoal(goalResolvers, action);
  const reason = 'Starting';
  goalResolvers = reject(goalResolvers, stop, reason);
  return {
    ...state,
    goalResolvers,
  };
}

export function onStop(state: State, action: Stop): State {
  let goalResolvers = state.goalResolvers;
  goalResolvers = addGoal(goalResolvers, action);
  const reason = 'Stopping';
  goalResolvers = reject(goalResolvers, start, reason);
  goalResolvers = reject(goalResolvers, restart, reason);
  return {
    ...state,
    goalResolvers,
  };
}

export function onTaskFailed(state: State): State {
  // What we do to recover is really going to be dependent on what the failure is.
  let goalResolvers = state.goalResolvers;
  if (state.replication) {
    // V1 would trigger a restart, which is not a bad place to begin.
    // Make sure that it doesn't cause an infinite loop though.
    if (state.currentTask?.kind !== activate && state.currentTask?.kind !== suspend) {
      goalResolvers = addGoal(goalResolvers, createGoal(restart));
    }
  }
  return {
    ...state,
    cleaned: false,
    goalResolvers,
    currentTask: undefined,
  };
}

export function selectBorer(state: State, action: SelectBorer): State {
  return {
    ...state,
    borerSelected: action.borerSelected,
  };
}

export function setOnline(state: State, action: NetworkChanged): State {
  return {
    ...state,
    online: action.online,
  };
}

export function taskStarted(state: State): State {
  return {
    ...state,
    currentTask: state.nextTask,
    nextTask: undefined,
  };
}

export function updateFeatureFlags(state: State, action: FeatureFlagsChanged): State {
  return {
    ...state,
    featureFlags: action.featureFlags,
  };
}

export const actionReducers: ActionReducers = {
  ActivateComplete: afterActivate,
  AddTrackingEvent: addTrackingEvent,
  CleanComplete: afterClean,
  Destroy: onDestroy,
  DestroyComplete: afterDestroy,
  Disable: onDisable,
  EventsTracked: clearTrackingEvents,
  FeatureFlagsChanged: updateFeatureFlags,
  InitializeComplete: afterInitialize,
  LogOut: onLogOut,
  NetworkChanged: setOnline,
  Rebuild: onRebuild,
  Restart: onRestart,
  SelectBorer: selectBorer,
  Start: onStart,
  StartTask: taskStarted,
  Stop: onStop,
  SuspendComplete: afterSuspend,
  TaskFailed: onTaskFailed,
  UpgradeComplete: afterUpgrade,
  WipeComplete: afterWipe,
};

export function reduceAction(oldState: State, action: Actions): State {
  // This is safe because `actionReducers` already mapped the kinds to the specific action types.
  const reducer = actionReducers[action.kind] as Reducer<State, typeof action>;
  return reducer(oldState, action);
}
